import { useCallback, useEffect, useState } from "react";
import {
  applyFeedbackPatch,
  Feedback,
  parseFeedbackCounts,
  parseFeedbackList,
  sortFeedbackForTriage,
  type FeedbackCounts,
  type Feedback as FeedbackT,
  type FeedbackFilter,
  type FeedbackPriority as FeedbackPriorityT,
  type FeedbackStatus as FeedbackStatusT,
} from "@heraion/shared";
import type { InboxApiFetch } from "./types";

export type FeedbackPatchBody = {
  priority?: FeedbackPriorityT | null;
  status?: FeedbackStatusT;
};

const cursorFrom = (raw: unknown): string | null => {
  const c = (raw as { cursor?: unknown }).cursor;
  return typeof c === "string" && c.length > 0 ? c : null;
};

// Data + mutation state for the admin inbox.
//   - The displayed list is fetched per active filter, paginated by an
//     opaque cursor (`loadMore` appends the next page).
//   - The filter-chip counts come from the SERVER's maintained counter
//     (authoritative totals, not a count of the loaded page) and are kept
//     across refetches so they never flicker to 0.
// apiFetch is the app-injected client and is expected to be stable.
export function useFeedbackInbox(apiFetch: InboxApiFetch) {
  const [items, setItems] = useState<FeedbackT[] | null>(null);
  const [counts, setCounts] = useState<FeedbackCounts | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filter, setFilter] = useState<FeedbackFilter>("open");
  const [busyId, setBusyId] = useState<string | null>(null);

  const pagePath = useCallback(
    (theFilter: FeedbackFilter, pageCursor: string | null): string => {
      const qs = new URLSearchParams();
      if (theFilter !== "all") qs.set("status", theFilter);
      if (pageCursor) qs.set("cursor", pageCursor);
      const q = qs.toString();
      return q ? `/api/feedback?${q}` : "/api/feedback";
    },
    [],
  );

  // First page on mount + whenever the filter changes. Stale-while-
  // revalidate on counts: keep the last server counts during the fetch.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const raw = await apiFetch<unknown>(pagePath(filter, null));
        if (cancelled) return;
        setItems(parseFeedbackList(raw));
        setCursor(cursorFrom(raw));
        const c = parseFeedbackCounts(raw);
        if (c) setCounts(c);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "load_failed");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiFetch, filter, pagePath]);

  // Append the next page. No-op when there's no cursor (last page).
  const loadMore = useCallback(async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const raw = await apiFetch<unknown>(pagePath(filter, cursor));
      const next = parseFeedbackList(raw);
      setItems((prev) => sortFeedbackForTriage([...(prev ?? []), ...next]));
      setCursor(cursorFrom(raw));
      const c = parseFeedbackCounts(raw);
      if (c) setCounts(c);
    } catch (e) {
      setError(e instanceof Error ? e.message : "load_failed");
    } finally {
      setLoadingMore(false);
    }
  }, [apiFetch, filter, cursor, loadingMore, pagePath]);

  const patch = useCallback(
    async (id: string, body: FeedbackPatchBody) => {
      setBusyId(id);
      setError(null);
      // Capture the pre-patch status so we can mirror the server's counter
      // move locally (the server already adjusted its counter in the same
      // transaction) without a refetch.
      const prevStatus = items?.find((it) => it.id === id)?.status ?? null;
      try {
        // ⛔ Deliberately NOT on the durableWrite seam (DX-2/E): this parses
        // the server's updated row to move the counters, and a queued write
        // has no response to parse. Admin triage is also an online gesture
        // by nature. See the exceptions note in ../seams.ts.
        const updated = await apiFetch<unknown>(
          `/api/feedback/${encodeURIComponent(id)}`,
          { method: "PATCH", body },
        );
        const parsed = Feedback.safeParse(updated);
        if (!parsed.success) {
          setError("schema_drift");
          return;
        }
        setItems((prev) =>
          prev === null ? prev : applyFeedbackPatch(prev, parsed.data, filter),
        );
        const newStatus = parsed.data.status;
        if (prevStatus && newStatus !== prevStatus) {
          setCounts((c) =>
            c
              ? { ...c, [prevStatus]: c[prevStatus] - 1, [newStatus]: c[newStatus] + 1 }
              : c,
          );
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : "patch_failed");
      } finally {
        setBusyId(null);
      }
    },
    [apiFetch, filter, items],
  );

  return {
    items,
    counts,
    error,
    loading,
    loadingMore,
    hasMore: cursor !== null,
    filter,
    setFilter,
    busyId,
    patch,
    loadMore,
  };
}
